import { useIntl } from 'react-intl';
import { useEffect, useRef, useState } from 'react';
import { StringParam, useQueryParams } from 'use-query-params';

import { searchGlobal } from 'api/algolia';
import { useDebounce } from 'utils/use-debounce';
import { useWindowSize } from 'utils/use-window-size';
import { ISortOption } from 'components/sort';
import { DATE_FILTER_TYPE, DateFilterValue } from 'components/date-filter-button/date-filter-data';
import {
	DATE_FILTER_QUERY_KEY,
	DateFilterParam,
	useDateFilterButton
} from 'components/date-filter-button/use-date-filter-button';

import IcnDate from 'public/assets/icons/event.svg';
import { TYPE_QUERY_KEY, TypeParam, useType } from './queries';

import styles from './search-overview.module.scss';
import { Icon } from 'components/icon/icon';
import { useRouter } from 'next/router';
import MobileActions from './mobile-actions';
import { BaseModule } from 'modules/base-module/base-module';
import SearchHeader from 'modules/search-header/search-header';
import { DefaultModuleOptionsFieldsFragment } from 'api/graphql-types';
import { getInternalNotes } from 'utils/global-search';
import { useSession } from 'next-auth/react';
import { getSite } from 'utils/subsite';
import { EmptyState } from 'components/empty-state/empty-state';
import { SEARCH_QUERY_KEY, SORT_QUERY_KEY, NewOldSortParam, useSearch, useSort } from 'utils/hooks/queries';
import { sanitizeAndParseHtml } from 'utils/rich-text-renderer/sanitize-and-parse';
import { SearchResultCard } from './search-result-card';

type FetchSearchResults = (
	newSort: ISortOption,
	newSearchValue: string | null,
	startDate?: Date,
	endDate?: Date
) => Promise<void>;

type searchResultFetchProps = {
	sort: ISortOption;
	searchValue: string;
	startDate?: Date | null;
	endDate?: Date | null;
};

export interface ISearchOverviewProps {
	resultLabel: string;
	header: {
		searchPlaceholder: string;
		headerTitle: string;
		fullPath: string;
	};
	headerDefaultOptions: DefaultModuleOptionsFieldsFragment;
}

const SearchResultOverview = ({
	resultLabel,
	header,
	headerDefaultOptions
}: ISearchOverviewProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { locale } = useRouter();
	const { data: session } = useSession();

	const [query, setQuery] = useQueryParams({
		[SORT_QUERY_KEY]: NewOldSortParam,
		[SEARCH_QUERY_KEY]: StringParam,
		[DATE_FILTER_QUERY_KEY]: DateFilterParam,
		[TYPE_QUERY_KEY]: TypeParam
	});

	const overviewHeaderRef = useRef<HTMLDivElement>(null);

	const [requests, setRequests] = useState<any[]>([]);
	const [requestIsLoading, setRequestIsLoading] = useState<boolean>(true);

	const [sortIsOpen, setSortIsOpen] = useState<boolean>(false);
	const [filtersIsOpen, setFiltersIsOpen] = useState<boolean>(false);
	const [initialQuery, setInitialQuery] = useState<typeof query | undefined>();
	const [hasFiltered, setHasFiltered] = useState<boolean>(false);

	const { sort, setSort, Sort } = useSort('search', 'newOld');
	const { searchValue, setSearchValue } = useSearch();
	const { dateFilter, setDateFilter, DateFilterButton } = useDateFilterButton();
	const { selectedType } = useType();

	const [search, setSearch] = useState<string | null>(searchValue);
	const debouncedSearch = useDebounce(search, 800);

	const [internalNotes, setInternalNotes] = useState<any[]>([]);

	const windowSize = useWindowSize(100);
	const MOBILE_BREAKPOINT = 768;

	// Close mobile dropdowns when resizing to desktop
	useEffect(() => {
		if ((windowSize.width || 0) >= MOBILE_BREAKPOINT) {
			setSortIsOpen(false);
			setFiltersIsOpen(false);
		}
	}, [windowSize.width]);

	const fetchSearchResults: FetchSearchResults = async (newSort, newSearchValue, startDate, endDate): Promise<void> => {
		if (!newSearchValue) {
			setRequestIsLoading(false);
			return;
		}

		setRequestIsLoading(true);

		try {
			const { hostname } = window.location;
			const site = getSite(hostname);

			// Use Algolia for main search results (pages/content)
			const { results } = await searchGlobal(
				newSearchValue || '',
				{
					startDate,
					endDate
				},
				{ sort: newSort?.id === 'asc' ? 'asc' : 'desc', limit: 9999, locale },
				site as any
			);

			setRequests(results);

			if (session?.accessToken) {
				try {
					const internalNotesResponse = await getInternalNotes(newSearchValue, session);
					setInternalNotes(internalNotesResponse);
				} catch (internalNotesError) {
					// eslint-disable-next-line no-console
					console.error('SEARCH_OVERVIEW_internalNotes_error', internalNotesError);
					setInternalNotes([]);
				}
			} else {
				setInternalNotes([]);
			}
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('SEARCH_OVERVIEW_error', error);
			setRequests([]);
		}

		setRequestIsLoading(false);
	};

	const handleDeleteFilter = (): void => {
		setQuery({
			datum: null,
			sort: null,
			type: null
		});

		handleFetchResults(undefined, true);
	};

	const handleDateOnChange = (value: DateFilterValue): void => {
		if (!setDateFilter) return;
		setDateFilter(value);

		const isAll = value.type === DATE_FILTER_TYPE.ALL;
		const endDate = isAll ? null : value.endDate;
		const startDate = isAll ? null : value.startDate;

		handleFetchResults({ startDate, endDate });
	};

	const handleSortOnChange = (selectedSortFilter: { id: string; label: string } | undefined): void => {
		if (selectedSortFilter) {
			setSort(selectedSortFilter);
			handleFetchResults({ sort: selectedSortFilter });
		}
	};

	const handleSearchOnChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
		setSearch(event.target.value);
	};

	const handleFetchResults = async (data?: Partial<searchResultFetchProps>, reset?: boolean): Promise<void> => {
		const selectedSort = reset ? { id: '', label: '' } : data?.sort || sort;
		const selectedSearch = reset ? '' : data?.searchValue || debouncedSearch;

		const selectedStartDate = reset
			? undefined
			: data?.startDate === undefined
			? dateFilter?.startDate
			: data?.startDate;

		const selectedEndDate = reset ? undefined : data?.endDate === undefined ? dateFilter?.endDate : data?.endDate;

		setHasFiltered(data !== undefined);
		fetchSearchResults(selectedSort, selectedSearch, selectedStartDate || undefined, selectedEndDate || undefined);
	};

	useEffect(() => {
		handleFetchResults(undefined);
	}, [initialQuery]);

	useEffect(() => {
		setSearchValue(debouncedSearch || null);

		if (debouncedSearch !== null) {
			handleFetchResults();
		}
	}, [debouncedSearch]);

	useEffect(() => {
		if (
			!hasFiltered &&
			!initialQuery &&
			(query[DATE_FILTER_QUERY_KEY] || query[SEARCH_QUERY_KEY] || query[SORT_QUERY_KEY])
		) {
			setInitialQuery(query);
		}
	}, [query]);

	useEffect(() => {
		if (!!query[SEARCH_QUERY_KEY] && initialQuery?.[SEARCH_QUERY_KEY] !== query[SEARCH_QUERY_KEY]) {
			setSearch(query[SEARCH_QUERY_KEY]);
		}
	}, [query[SEARCH_QUERY_KEY]]);

	return (
		<>
			<BaseModule defaultModuleOptions={headerDefaultOptions}>
				<SearchHeader
					searchPlaceholder={header?.searchPlaceholder}
					headerTitle={header.headerTitle}
					fullPath={header.fullPath}
					searchResultItems={requests}
					internalNotes={internalNotes}
					isLoading={requestIsLoading}
					search={search || ''}
					handleSearchOnChange={handleSearchOnChange}
				/>
			</BaseModule>
			<BaseModule
				defaultModuleOptions={{
					backgroundColor: 'grey',
					padding: 'small'
				}}
			>
				<div className={styles.SearchOverview} ref={overviewHeaderRef}>
					<div className={styles.wrapper}>
						<>
							<MobileActions
								filtersIsOpen={filtersIsOpen}
								setFiltersIsOpen={setFiltersIsOpen}
								sortIsOpen={sortIsOpen}
								setSortIsOpen={setSortIsOpen}
								filterHeader={formatMessage({ id: 'modules.searchOverview.dateFilter' })}
								filterIcon={<Icon customIcon={<IcnDate />} size="16px" />}
							/>
							<div className={styles.filterBar}>
								<span className={styles.label}>{resultLabel}</span>
								{!!(debouncedSearch as string) && (
									<div className={styles.sortBar}>
										<DateFilterButton
											onChange={handleDateOnChange}
											title="Datum"
											mobileIsOpen={filtersIsOpen}
											setMobileIsOpen={setFiltersIsOpen}
										/>
										<Sort mobileIsOpen={sortIsOpen} setMobileIsOpen={setSortIsOpen} onChange={handleSortOnChange} />
									</div>
								)}
							</div>
						</>
						{requestIsLoading ? (
							<div className={styles.results}>
								<section className={styles.content}>
									<div className={styles.card} style={{ display: 'flex', flexDirection: 'column' }}>
										<div className={styles.titleHeader}>
											<div className={styles.loadingBar} style={{ width: '60%' }} />
										</div>
										<div className={styles.description}>
											<div className={styles.loadingBarDescription} />
										</div>
										<div className={styles.bottomRow}>
											<div className={styles.loadingBar} style={{ width: '40%' }} />
										</div>
									</div>
								</section>
							</div>
						) : (
							<div className={styles.results}>
								<section className={styles.content}>
									{!requests?.length && !internalNotes?.length ? (
										<div className={styles.emptyStateCard}>
											<div className={styles.emptyState}>
												{searchValue === null
													? formatMessage({ id: 'modules.search.emptystateNoSearch' })
													: formatMessage({ id: 'modules.search.emptystate' })}
											</div>
										</div>
									) : (
										<>
											{selectedType?.id === 'pages' &&
												(requests.length > 0 ? (
													requests?.map((item, idx) => {
														const link = item.slug;
														const href = link || item.url || item.link.linkUrl;

														return (
															<SearchResultCard
																key={idx}
																item={item}
																link={link}
																href={href}
																index={idx}
																formatMessage={formatMessage}
															/>
														);
													})
												) : (
													<div>
														<EmptyState onClearFilters={handleDeleteFilter} isLoading={requestIsLoading} />
													</div>
												))}
											{selectedType?.id === 'internal' &&
												internalNotes?.map((item, idx) => (
													<SearchResultCard
														key={idx}
														item={{
															...item,
															description: sanitizeAndParseHtml(item.description || '')
														}}
														link={item.slug}
														href={item.slug}
														index={idx}
														formatMessage={formatMessage}
													/>
												))}
										</>
									)}
								</section>
							</div>
						)}
					</div>
				</div>
			</BaseModule>
		</>
	);
};

export default SearchResultOverview;
